import React, { useState, useEffect, useMemo } from 'react';
import { Building, Download, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import reportService from '../../services/report/reportService';
import api from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { SearchableSelect } from '../Common/SearchableSelect';
import type { AccountReportData, AccountReportAccount } from '../../types/report';

const AccountReport: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [accountId, setAccountId] = useState<number | undefined>(undefined);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [reportData, setReportData] = useState<AccountReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const response = await api.getAccounts();
      setAccounts(response.data || []);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await reportService.generateAccountReport({
        start_date: startDate,
        end_date: endDate,
        account_id: accountId
      });
      setReportData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setLoading(true);
    try {
      await reportService.downloadAccountPdf({
        start_date: startDate,
        end_date: endDate,
        account_id: accountId
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await reportService.downloadAccountExcel({
        start_date: startDate,
        end_date: endDate,
        account_id: accountId
      });
    } catch (err: any) {
      setError(err.message || 'Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const toggleAccountExpansion = (accountIdToToggle: number) => {
    setExpandedAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountIdToToggle)) {
        newSet.delete(accountIdToToggle);
      } else {
        newSet.add(accountIdToToggle);
      }
      return newSet;
    });
  };

  // Transform accounts to options format for SearchableSelect
  const accountOptions = useMemo(() => {
    return accounts.map(account => ({
      id: account.id,
      label: account.account_name || 'Unnamed Account'
    }));
  }, [accounts]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Building className="h-7 w-7 mr-3 text-primary-600" />
          Account Commission Report
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Generate commission report for trading accounts by date range
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="account" className="block text-sm font-medium text-gray-700 mb-2">
              Account (Optional)
            </label>
            <SearchableSelect
              options={accountOptions}
              value={accountId}
              onChange={setAccountId}
              placeholder="accounts"
              allLabel="All Accounts"
            />
          </div>

          <div className="flex items-end gap-3">
            <button
              onClick={handleGenerateReport}
              disabled={loading || !startDate || !endDate}
              className="flex-1 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate
            </button>

            {reportData && (
              <>
                <button
                  onClick={handleDownloadPdf}
                  disabled={loading}
                  className="flex-1 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  PDF
                </button>
                <button
                  onClick={handleDownloadExcel}
                  disabled={downloadingExcel}
                  className="flex-1 bg-emerald-700 text-white px-6 py-2 rounded-lg hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloadingExcel ? 'Downloading...' : 'Excel'}
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Generating report...</p>
          </div>
        </div>
      )}

      {/* Report Data */}
      {!loading && reportData && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Period</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(reportData.date_range.start_date)} to {formatDate(reportData.date_range.end_date)}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Gross Commission</p>
                <p className="text-lg font-semibold text-green-700">
                  {formatCurrency(reportData.summary.gross_commission)}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Salesman Commission</p>
                <p className="text-lg font-semibold text-blue-700">
                  {formatCurrency(reportData.summary.salesman_commission ?? reportData.summary.user_commission ?? 0)}
                </p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">House Commission</p>
                <p className="text-lg font-semibold text-amber-700">
                  {formatCurrency(reportData.summary.house_commission ?? reportData.summary.net_commission ?? 0)}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Accounts</p>
                <p className="text-lg font-semibold text-gray-900">
                  {reportData.summary.account_count}
                </p>
              </div>
            </div>
          </div>

          {/* Accounts */}
          {reportData.accounts.map((account) => (
            <AccountCard
              key={account.account_id}
              account={account}
              expanded={expandedAccounts.has(account.account_id)}
              onToggle={() => toggleAccountExpansion(account.account_id)}
              formatCurrency={formatCurrency}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface AccountCardProps {
  account: AccountReportAccount;
  expanded: boolean;
  onToggle: () => void;
  formatCurrency: (amount: number) => string;
}

const AccountCard: React.FC<AccountCardProps> = ({ account, expanded, onToggle, formatCurrency }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Account Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1 text-left">
          <h3 className="text-base font-semibold text-gray-900">
            {account.account_name} - {account.client_name}
          </h3>
          <div className="mt-2 grid grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Transactions</p>
              <p className="text-sm font-medium text-gray-900">{account.transaction_count}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Gross</p>
              <p className="text-sm font-medium text-green-700">{formatCurrency(account.gross_commission)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Salesman</p>
              <p className="text-sm font-medium text-blue-700">{formatCurrency(account.salesman_commission ?? account.user_commission ?? 0)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">House</p>
              <p className="text-sm font-medium text-amber-700">{formatCurrency(account.house_commission ?? account.net_commission ?? 0)}</p>
            </div>
          </div>
        </div>
        <div className="ml-4">
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Transactions Table */}
      {expanded && (
        <div className="border-t border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Bought</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sold</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Security</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Comm.</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Comm. %</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">User Comm.</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employees</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {account.transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">{formatDate(transaction.date)}</td>
                    <td className="px-3 py-3 text-sm text-right text-gray-900">{transaction.bought > 0 ? transaction.bought.toLocaleString() : ''}</td>
                    <td className="px-3 py-3 text-sm text-right text-gray-900">{transaction.sold > 0 ? transaction.sold.toLocaleString() : ''}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{transaction.security}</td>
                    <td className="px-3 py-3 text-sm text-right text-gray-900">{formatCurrency(transaction.price)}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{transaction.account_name || account.account_name}</td>
                    <td className="px-3 py-3 text-sm text-right text-gray-600">{(transaction.rate ?? 0).toFixed(4)}</td>
                    <td className="px-3 py-3 text-sm text-right font-medium text-blue-700">{formatCurrency(transaction.gross_commission || 0)}</td>
                    <td className="px-3 py-3 text-sm text-right text-gray-600">
                      {transaction.employees?.map((emp, idx) => (
                        <span key={emp.user_id}>
                          {(emp.percentage ?? 0).toFixed(2)}%
                          {idx < (transaction.employees?.length || 0) - 1 && ', '}
                        </span>
                      ))}
                    </td>
                    <td className="px-3 py-3 text-sm text-right font-medium text-green-700">{formatCurrency(transaction.salesman_commission ?? transaction.user_commission ?? 0)}</td>
                    <td className="px-3 py-3 text-sm text-gray-600">
                      {transaction.employees?.map((emp, idx) => (
                        <span key={emp.user_id}>
                          {emp.user_name}
                          {idx < (transaction.employees?.length || 0) - 1 && ', '}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountReport;
